import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useFormik, FormikProvider, FieldArray } from 'formik';

// styles
import './SplitJobDay.css'

// utils
import { createSplitJobDaySubmitData, findForemanAssignmentId } from './SplitJobDay.page.utils';
import { validate } from '../../../shared/utility';

// queries
import { apolloClient } from '../../../index';
import { useMutation } from '@apollo/client';
import { GET_JOB_DAY_BY_ID_FOR_SPLIT_ASSIGNMENTS, GET_JOB_DAY_CREW_ASSIGNMENTS, GET_JOB_DAY_BY_ID_WITH_JOB_INFO } from '../../../graphql/jobDay/jobDay.queries';
import { SPLIT_JOB_DAY } from '../../../graphql/jobDay/jobDay.mutations';

// components
import ActionButton from '../../../components/buttons/ActionButton/ActionButton.component';
import Heading from '../../../components/text/Heading/Heading.component';
import SplitDayScopeInput from '../../../components/forms/lists/SplitDayScopeInput/SplitDayScopeInput.component';
import SplitDayAssignmentInput from '../../../components/forms/lists/SplitDayAssignmentInput/SplitDayAssignmentInput.component';

// collections
import JobDayInfo from './collections/JobDayInfo/JobDayInfo.collection';

// mui
import { Grid, Container, Typography, ButtonGroup } from '@mui/material';

const TWO_OPTIONS = [
    { value: 'A', label: 'A'},
    { value: 'B', label: 'B'}
]

const THREE_OPTIONS = [
    { value: 'A', label: 'A'},
    { value: 'B', label: 'B'},
    { value: 'C', label: 'C'}
]

const CLASSIFICATION_OPTIONS = {
    J: 'Journeyman',
    F: 'Foreman',
    S: 'Superintendent',
    A_1: 'A1',
    A_2: 'A2',
    A_3: 'A3',
    A_4: 'A4',
    A_5: 'A5',
    A_6: 'A6',
    A_7: 'A7',
    A_8: 'A8'
}

const validationSchema = {
    foremanAssignmentId: {
        type: 'input',
        validation: {
            required: true
        }
    },
    assignments: {
        type: "list",
        fields: {
            option: {
                type: "input",
                validation: {
                    required: true
                }
            }
        }
    },
    days: {
        type: "list",
        fields: {
            scope: {
                type: "input",
                validation: {
                    required: true
                }
            }
        }
    }
}

export default function SplitJobDay() {
    const { jobDayId } = useParams();
    const jobDayIdInt = parseInt(jobDayId, 10);
    const [isFetchingObject, setIsFetchingObject] = useState(true);
    const [stage, setStage] = useState(1);
    const [splitAmount, setSplitAmount] = useState(0);
    const [selectedJobDay, setSelectedJobDay] = useState(null);
    const [splitJobDayMutation] = useMutation(SPLIT_JOB_DAY);
    const history = useHistory();
    const [errors, setErrors] = useState({ assignments: [], days: [] });

    const formik = useFormik({
        initialValues: {
            foremanAssignmentId: null,
            assignments: [],
            days: []
        }
    });

    if (isFetchingObject) {
        apolloClient.query({ 
            query: GET_JOB_DAY_BY_ID_FOR_SPLIT_ASSIGNMENTS, 
            variables: { id: jobDayIdInt }
        })
        .catch(e => {
            console.error(e);
        })
        .then(result => {
            if (result && result.data) {
                setSelectedJobDay(result.data.jobDay);
                setIsFetchingObject(false);
                
                const foremanId = findForemanAssignmentId(result.data.jobDay.crewMemberAssignments);
                formik.setFieldValue('foremanAssignmentId', foremanId);
                
                // Add default 'option' property to each assignment
                const assignmentsWithOption = result.data.jobDay.crewMemberAssignments.map(assignment => ({
                    ...assignment,
                    option: 'A'
                }));
                
                formik.setFieldValue('assignments', assignmentsWithOption);
                
                console.log('Foreman ID found:', foremanId);
                console.log('Assignments with options:', assignmentsWithOption);
            }
        });
    }

    const handleSubmit = () => {
        console.log('handleSubmit called');
        console.log('formik.values:', formik.values);
        
        const { isValid, errors: validationErrors } = validate(formik.values, validationSchema);
        
        console.log('isValid:', isValid);
        console.log('validation errors:', validationErrors);
        
        if (isValid) {
            const finalData = {
                ...createSplitJobDaySubmitData(formik.values.assignments, formik.values.days), 
                foremanAssignmentId: parseInt(formik.values.foremanAssignmentId, 10), 
                originalJobDayId: jobDayIdInt
            };
            
            console.log('finalData:', finalData);

            splitJobDayMutation({ 
                variables: { ...finalData }, 
                refetchQueries: [GET_JOB_DAY_CREW_ASSIGNMENTS, GET_JOB_DAY_BY_ID_WITH_JOB_INFO]
            })
            .catch(e => {
                console.error('Mutation error:', e);
            })
            .then(result => {
                console.log('Mutation result:', result);
                if (result && result.data) {
                    history.push(`/schedule/details/${jobDayIdInt}?splitJobDay=success`);
                }
            });
        } else {
            console.log('Validation failed with errors:', validationErrors);
            setErrors(validationErrors);
        }
    };

    let stageSection = null;
    let navigationButtons = null;

    switch (stage) {
        case 1:
            stageSection = (
                <div>
                    <JobDayInfo jobDayId={jobDayIdInt} />
                    <hr className='split-job-day-hr' />
                    <Container disableGutters sx={{ marginBottom: 4 }}>
                        <ButtonGroup variant='outlined'>
                            <ActionButton 
                                label="Split into Two" 
                                size='full' 
                                color='secondary' 
                                onClick={() => {
                                    setStage(2);
                                    setSplitAmount(2);
                                    const days = [
                                        { id: jobDayIdInt, scope: selectedJobDay?.scopeOfWork || '', jobDay: 'A' },
                                        { id: 0, scope: '', jobDay: 'B' }
                                    ];
                                    formik.setFieldValue('days', [...days]);
                                }} 
                            />
                            <ActionButton 
                                label="Split into Three" 
                                size='full' 
                                color='secondary' 
                                onClick={() => {
                                    setStage(2);
                                    setSplitAmount(3);
                                    const days = [
                                        { id: jobDayIdInt, scope: selectedJobDay?.scopeOfWork || '', jobDay: 'A' },
                                        { id: 0, scope: '', jobDay: 'B' },
                                        { id: 0, scope: '', jobDay: 'C' }
                                    ];
                                    formik.setFieldValue('days', [...days]);
                                }} 
                            />
                        </ButtonGroup>
                    </Container>
                </div>
            );

            navigationButtons = (
                <Container disableGutters sx={{ marginTop: 4, marginBottom: 4 }}>
                    <ActionButton label="Cancel" color='secondary' onClick={() => history.goBack()} />
                </Container>
            );
            break;

        case 2:
            stageSection = (
                <FieldArray 
                    name="days"
                    render={() => (
                        <React.Fragment>
                            {formik.values.days.map((day, index) => (
                                <SplitDayScopeInput 
                                    key={`day-scope-${index}`}
                                    id={index}
                                    prefix={`days.${index}`}
                                    value={day.scope}
                                    handleChange={formik.handleChange}
                                    errors={errors['days'].length > 0 ? errors['days'][index] : {}}
                                />
                            ))}
                        </React.Fragment>
                    )}
                />
            );

            navigationButtons = (
                <Container disableGutters sx={{ marginBottom: 4, marginTop: 4 }}>
                    <Grid container spacing={2}>
                        <Grid item>
                            <ActionButton label="Back" color='secondary' onClick={() => setStage(1)} />
                        </Grid>
                        <Grid item>
                            <ActionButton label="Next" onClick={() => setStage(3)} />
                        </Grid>
                    </Grid>
                </Container>
            );
            break;

        case 3:
            stageSection = (
                <React.Fragment>
                    {formik.values.days.map((day, index) => (
                        <Container key={`day-container-${index}`} disableGutters sx={{ marginBottom: 4 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography variant="h5">
                                        {index === 0 ? 'A - Scope' : index === 1 ? 'B - Scope' : 'C - Scope'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <p className='split-job-day-scope-section-scope'>{day.scope}</p>
                                </Grid>
                            </Grid>
                        </Container>
                    ))}

                    <hr className='split-job-day-hr' />

                    <Typography variant="h4">Assignments</Typography>

                    <FieldArray
                        name="assignments"
                        render={() => (
                            <React.Fragment>
                                {formik.values.assignments.map((assignment, index) => (
                                    <SplitDayAssignmentInput 
                                        key={`assignment-${assignment.id || index}`}
                                        id={index}
                                        value={assignment.option}
                                        options={splitAmount === 3 ? THREE_OPTIONS : TWO_OPTIONS}
                                        name={`${assignment.crewMember.firstName} ${assignment.crewMember.lastName}`}
                                        classification={CLASSIFICATION_OPTIONS[assignment.crewMember.classification]}
                                        handleChange={value => formik.setFieldValue(`assignments.${index}.option`, value)}
                                        errors={errors['assignments'].length > 0 ? errors['assignments'][index] : {}}
                                    />
                                ))}
                            </React.Fragment>
                        )}
                    />
                </React.Fragment>
            );

            navigationButtons = (
                <Container disableGutters sx={{ marginBottom: 4 }}>
                    <Grid container spacing={2}>
                        <Grid item>
                            <ActionButton label="Back" color='secondary' onClick={() => setStage(2)} />
                        </Grid>
                        <Grid item>
                            <ActionButton label="Save Changes" color='success' onClick={handleSubmit} />
                        </Grid>
                    </Grid>                
                </Container>
            );
            break;

        default:
            break;
    }

    return (
        <div>
            <Container disableGutters sx={{ marginBottom: 4 }}>
                <Grid container>
                    <Grid item>
                        <Typography variant="h2">Split Job Day</Typography>
                    </Grid>
                </Grid>
            </Container>

            <FormikProvider value={formik}>
                <form>
                    {stageSection}
                </form>
            </FormikProvider>

            {navigationButtons}
        </div>
    );
}
